import { AppSettings } from "@/AppSettings";
import { SourceDataFormStatus, SourceDataNotApplicable, SourceDataPriority, SourceDataReview, SourceDataVerification, UpdateSourceDataItemState } from "@/api/dtos";
import { IReviewAndVerificationItemData } from "@/components/forms/ReviewAndVerificationForm";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import useForm from "@/hooks/form/source-data/useForm";
import useItem from "@/hooks/form/source-data/useItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, useCallback, useContext, useMemo } from "react";
import { ButtonPlaceholder, CounterButton, CounterButtonBox, StyledTooltip } from ".";



const SourceDataFormReviewAndVerificationButton: FC<{ 
    form: ReturnType<typeof useForm>;
    itemData?: ReturnType<typeof useItem>;
    priority?: SourceDataPriority;
    hideReview?: boolean;
    hideVerification?: boolean;
    hideNotApplicable?: boolean;
    allowReview?: boolean;
    allowVerification?: boolean;
    allowNotApplicable?: boolean;
    itemGroupAllowReview?: boolean;
    itemGroupAllowVerification?: boolean;
    itemGroupAllowNotApplicable?: boolean;
}> = ({
    form,
    itemData,
    priority,
    hideReview,
    hideVerification,
    hideNotApplicable,
    allowReview,
    allowVerification,
    allowNotApplicable,
    itemGroupAllowReview,
    itemGroupAllowVerification,
    itemGroupAllowNotApplicable
}) => {
    const propertyPath = useMemo(() => {
        if (!itemData?.studyEventOid ||
            !itemData?.studyEventRepeat ||
            !itemData?.caseReportFormOid ||
            !itemData?.itemGroupRepeat ||
            !itemData?.itemName) {
            return 'data.unknown'
        }

        return `data.${itemData.studyEventOid}.${itemData.studyEventRepeat}.${itemData.caseReportFormOid}.${itemData?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey}.${itemData.itemGroupRepeat}.${itemData.itemName}`;
    }, [
        // itemData?.trial?.id,
        // itemData?.masterGroup?.id,
        // itemData?.coordinatingGroup?.id,
        // itemData?.institution?.id,
        // itemData?.participant?.id,
        itemData?.studyEventOid,
        itemData?.studyEventRepeat,
        itemData?.caseReportFormOid,
        itemData?.itemGroupName,
        itemData?.itemGroupRepeat,
        itemData?.itemName
    ])

    const { 
        state: {
            value: data 
        },
        context: {
            setValue: setData,
            getValue: getData
        }
    } = useScopedField<IReviewAndVerificationItemData>(propertyPath, { value: true });

    const { 
        state: {
            value: loading 
        } 
    } = useScopedField<boolean>('loading', { value: true });

    const disabled = !form?.sourceDataForm || form.sourceDataForm.status === SourceDataFormStatus.None || form.sourceDataForm.status === SourceDataFormStatus.Skipped;; //!itemData?.sourceDataItem;

    const reviewLoading = data?.reviewLoading;
    const verificationsLoading = data?.verificationLoading;
    const notApplicableLoading = data?.notApplicableLoading;

    const client = useContext(ServiceStackClientContext);

    const onReviewClick = useCallback(async () => {

        const current = getData();
        const newValue = !current?.review?.reviewed;
        const changeVerified = !!itemData?.configuration?.allowVerification && !newValue && current?.verification?.verified;
        const verificationValue = itemData?.configuration?.allowVerification ?
            changeVerified ?
                newValue :
                current?.verification?.verified:
            undefined;
        const notApplicableValue = itemData?.configuration?.allowNotApplicable 
            ? !!current?.notApplicable?.notApplicable
            : undefined;

        setData(c => {
            const newValue = {...c};

            newValue.reviewLoading = true;

            if (changeVerified) {
                newValue.verificationLoading = true;
            }

            return newValue;
        }, true, true, true, true);

        try {
            const response = await client.post(new UpdateSourceDataItemState({
                trialId: itemData?.trial?.id,
                masterGroupId: itemData?.masterGroup?.id,
                coordinatingGroupId: itemData?.coordinatingGroup?.id,
                institutionId: itemData?.institution?.id,
                participantId: itemData?.participant?.id,
                studyEventOid: itemData?.studyEventOid,
                studyEventRepeat: itemData?.studyEventRepeat,
                caseReportFormOid: itemData?.caseReportFormOid,
                caseReportFormVersionOid: itemData?.caseReportFormVersionOid,
                itemGroupName: itemData?.itemGroupName,
                itemGroupRepeat: itemData?.itemGroupRepeat,
                itemName: itemData?.itemName,
                value: itemData?.sourceDataItem?.value,
                priority: priority,
                reviewed: newValue,
                verified: verificationValue,
                notApplicable: notApplicableValue,
                missing: !itemData?.sourceDataItem
            }));

            setData(c => {
                c.review = new SourceDataReview({
                    ...c.review,
                    reviewed: newValue
                });

                c.reviewLoading = false;

                if (changeVerified) {
                    c.verification = new SourceDataVerification({
                        ...c.verification,
                        verified: newValue
                    });

                    c.verificationLoading = false;
                }

                return c;
            }, true, true, true, true);
        }
        catch {
            setData(c => {
                const newValue = {...c};

                newValue.reviewLoading = false;

                if (changeVerified) {
                    newValue.verificationLoading = false;
                }

                return newValue;
            }, true, true, true, true);
        }

    }, [getData, setData, client, priority, itemData?.configuration?.allowVerification, itemData?.configuration?.allowNotApplicable]);


    const onVerifyClick = useCallback(async () => {
        const current = getData();
        const newValue = !current?.verification?.verified;
        const changeReview = !!itemData?.configuration?.allowReview && newValue && !current?.review?.reviewed;
        const reviewValue = itemData?.configuration?.allowVerification ?
            changeReview ?
                newValue :
                current?.review?.reviewed:
            undefined
        const notApplicableValue = itemData?.configuration?.allowNotApplicable 
            ? !!current?.notApplicable?.notApplicable
            : undefined;

        setData(c => {
            const newValue = {...c};

            newValue.verificationLoading = true;

            if (changeReview) {
                newValue.reviewLoading = true;
            }

            return newValue;
        }, true, true, true, true);

        try {
            const response = await client.post(new UpdateSourceDataItemState({
                trialId: itemData?.trial?.id,
                masterGroupId: itemData?.masterGroup?.id,
                coordinatingGroupId: itemData?.coordinatingGroup?.id,
                institutionId: itemData?.institution?.id,
                participantId: itemData?.participant?.id,
                studyEventOid: itemData?.studyEventOid,
                studyEventRepeat: itemData?.studyEventRepeat,
                caseReportFormOid: itemData?.caseReportFormOid,
                caseReportFormVersionOid: itemData?.caseReportFormVersionOid,
                itemGroupName: itemData?.itemGroupName,
                itemGroupRepeat: itemData?.itemGroupRepeat,
                itemName: itemData?.itemName,
                value: itemData?.sourceDataItem?.value,
                priority: priority,
                verified: newValue,
                reviewed: reviewValue,
                notApplicable: notApplicableValue,
                missing: !itemData?.sourceDataItem
            }));

            setData(c => {
                c.verification = new SourceDataVerification({
                    ...c.verification,
                    verified: newValue
                });

                c.verificationLoading = false;
    
                if (changeReview) {
                    c.review = new SourceDataReview({
                        ...c.review,
                        reviewed: newValue
                    });

                    c.reviewLoading = false;
                }

                return c;
            }, true, true, true, true);
        }
        catch {
            setData(c => {
                const newValue = {...c};

                newValue.verificationLoading = false;

                if (changeReview) {
                    newValue.reviewLoading = false;
                }

                return newValue;
            }, true, true, true, true);
        }

    }, [getData, setData, client, priority, itemData?.configuration?.allowReview, itemData?.configuration?.allowNotApplicable]);


    const onNotApplicableClick = useCallback(async () => {
        const current = getData();
        const newValue = !current?.notApplicable?.notApplicable;
        const changeReview = !!itemData?.configuration?.allowReview && newValue && current?.review?.reviewed;
        const changeVerification = !!itemData?.configuration?.allowVerification && newValue && current?.verification?.verified;
        const reviewValue = itemData?.configuration?.allowNotApplicable ?
            changeReview ?
                false :
                current?.review?.reviewed:
            undefined;
        const verificationValue = itemData?.configuration?.allowNotApplicable ?
            changeVerification ?
                false :
                current?.verification?.verified:
            undefined;

        setData(c => {
            const newValue = {...c};

            newValue.notApplicableLoading = true;

            if (changeReview) {
                newValue.reviewLoading = true;
            }

            if (changeVerification) {
                newValue.verificationLoading = true;
            }

            return newValue;
        }, true, true, true, true);

        try {
            const response = await client.post(new UpdateSourceDataItemState({
                trialId: itemData?.trial?.id,
                masterGroupId: itemData?.masterGroup?.id,
                coordinatingGroupId: itemData?.coordinatingGroup?.id,
                institutionId: itemData?.institution?.id,
                participantId: itemData?.participant?.id,
                studyEventOid: itemData?.studyEventOid,
                studyEventRepeat: itemData?.studyEventRepeat,
                caseReportFormOid: itemData?.caseReportFormOid,
                caseReportFormVersionOid: itemData?.caseReportFormVersionOid,
                itemGroupName: itemData?.itemGroupName,
                itemGroupRepeat: itemData?.itemGroupRepeat,
                itemName: itemData?.itemName,
                value: itemData?.sourceDataItem?.value,
                priority: priority,
                verified: verificationValue,
                reviewed: reviewValue,
                notApplicable: newValue,
                missing: !itemData?.sourceDataItem
            }));

            setData(c => {
                c.notApplicable = new SourceDataNotApplicable({
                    ...c.notApplicable,
                    notApplicable: newValue
                });

                c.notApplicableLoading = false;
    
                if (changeVerification) {
                    c.verification = new SourceDataVerification({
                        ...c.verification,
                        verified: verificationValue
                    });

                    c.verificationLoading = false;
                }
    
                if (changeReview) {
                    c.review = new SourceDataReview({
                        ...c.review,
                        reviewed: reviewValue
                    });

                    c.reviewLoading = false;
                }

                return c;
            }, true, true, true, true);
        }
        catch {
            setData(c => {
                const newValue = {...c};

                newValue.notApplicableLoading = false;

                if (changeVerification) {
                    newValue.verificationLoading = false;
                }

                if (changeReview) {
                    newValue.reviewLoading = false;
                }

                return newValue;
            }, true, true, true, true);
        }

    }, [getData, setData, client, priority, itemData?.configuration?.allowReview, itemData?.configuration?.allowVerification, itemData?.configuration?.allowNotApplicable]);

    // useEffect(() => {
    //     setReviewed(itemData?.review?.reviewed);
    // }, [itemData?.review, setReviewed])
    
    // useEffect(() => {
    //     setVerified(itemData?.verification?.verified);
    // }, [itemData?.verification, setVerified])
    return (
        <>
            {
                !hideReview && allowReview?
                    itemData?.configuration?.allowReview && itemGroupAllowReview && !data?.notApplicable?.notApplicable ?
                        <Box>
                            <StyledTooltip
                                title={
                                    disabled ? 
                                        'Form does not exist in OpenClinica' :
                                        data?.review?.reviewed ? 
                                            'Reviewed' : 
                                            'Not Reviewed'
                                }
                                followCursor
                            >
                                <CounterButtonBox>
                                    <CounterButton
                                        variant={
                                            data?.review?.reviewed ?
                                                'contained' :
                                                'outlined'
                                        }
                                        color={
                                            data?.review?.reviewed ?
                                                'success' :
                                                'primary'
                                        }
                                        loading={!!reviewLoading || !!loading}
                                        disabled={disabled}
                                        onClick={onReviewClick}
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.review} size="2x" />
                                    </CounterButton>
                                </CounterButtonBox>
                            </StyledTooltip>
                        </Box>:
                        <ButtonPlaceholder /> :
                    null
            }
            {
                !hideVerification && allowVerification ?
                    itemData?.configuration?.allowVerification && itemGroupAllowVerification && !data?.notApplicable?.notApplicable ?
                        <Box>
                            <StyledTooltip
                                title={
                                    disabled ? 
                                        'Form does not exist in OpenClinica' :
                                        data?.verification?.verified ? 
                                            'Verified' : 
                                            'Not Verified'
                                }
                                followCursor
                            >
                                <CounterButtonBox>
                                    <CounterButton
                                        variant={
                                            data?.verification?.verified ?
                                                'contained' :
                                                'outlined'
                                        }
                                        color={
                                            data?.verification?.verified ?
                                                'success' :
                                                'primary'
                                        }
                                        loading={!!verificationsLoading || !!loading}
                                        onClick={onVerifyClick}
                                        disabled={disabled}
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.verification} size="2x" />
                                    </CounterButton>
                                </CounterButtonBox>
                            </StyledTooltip>
                        </Box> :
                        <ButtonPlaceholder /> :
                    null
            }
            {
                !hideNotApplicable && allowNotApplicable ?
                    itemData?.configuration?.allowNotApplicable && itemGroupAllowNotApplicable ?
                        <Box>
                            <StyledTooltip
                                title={
                                    disabled ? 
                                        'Form does not exist in OpenClinica' :
                                        data?.notApplicable?.notApplicable ? 
                                            'Not Applicable' : 
                                            'Applicable'
                                }
                                followCursor
                            >
                                <CounterButtonBox>
                                    <CounterButton
                                        variant={
                                            data?.notApplicable?.notApplicable ?
                                                'contained' :
                                                'outlined'
                                        }
                                        color={
                                            data?.notApplicable?.notApplicable ?
                                                'success' :
                                                'primary'
                                        }
                                        loading={!!notApplicableLoading || !!loading}
                                        onClick={onNotApplicableClick}
                                        disabled={disabled}
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.notApplicable} size="2x" />
                                    </CounterButton>
                                </CounterButtonBox>
                            </StyledTooltip>
                        </Box> :
                        <ButtonPlaceholder /> :
                    null
            }
        </>
    );
};

export default SourceDataFormReviewAndVerificationButton;